import { useCallback, useEffect, useState } from 'react'

import { api } from '../../lib/api'
import { roundLabel } from '../../lib/approvals'
import type { Round } from '../../lib/approvals'
import { formatDate } from '../../lib/datetime'
import { errorText } from '../../lib/offsite'

/* ============================================================================
   ชิ้นส่วนที่หน้า "ของฉัน" ทั้งสามหน้าใช้ร่วมกัน
   API: backend/src/routes/me.js (อ่านอย่างเดียว · ยืนตัวตนด้วย cookie session)

   ⚠️ ทุกหน้าในหมวดนี้ดู **ข้อมูลของตัวเองเท่านั้น** — ไม่มีช่องให้ระบุรหัสพนักงาน
      ฝั่ง backend อ่าน empCode จาก session ไม่ใช่จาก query (ดูหัวไฟล์ me.js)
   ============================================================================ */

/** โหลดข้อมูลจาก /api/me/* พร้อมสถานะ loading/error — หน้าไหนก็ใช้ท่าเดียวกัน */
export function useMeData<T>(path: string, query: string) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await api<T>(`/api/me/${path}${query}`))
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [path, query])

  useEffect(() => {
    void load()
  }, [load])

  return { data, loading, error, reload: load }
}

/* ⚠️ สองฟังก์ชันล่างรับ unknown ไม่ใช่ string — ถ้าค่าที่มาไม่ใช่สตริงแล้วเรียก
   .split() ตรง ๆ ทั้งหน้าจะพังเป็น "TypeError: e.split is not a function"
   (เกิดจริงมาแล้ว: worktime.monthsAvailable คืน [{month, days}] ไม่ใช่ [string])
   เสียรูปแบบวันที่ดีกว่าเสียทั้งหน้า */

/** 'YYYY-MM' → 'ตุลาคม 2026' (ชื่อเดือนไทย + ปี ค.ศ. ตามกติกา datetime.ts) */
export function monthLabel(month: unknown): string {
  if (typeof month !== 'string') return String(month ?? '')
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  const names = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
  ]
  return `${names[m - 1] ?? m} ${y}`
}

/** 'YYYY-MM-DD' → '2026-10-13' (ค.ศ. ตามกติกา datetime.ts) */
export function dayLabel(date: unknown): string {
  if (typeof date !== 'string') return String(date ?? '')
  return formatDate(date)
}

/** เลขวันในสัปดาห์จาก backend (0 = อาทิตย์ ตาม getUTCDay) → 'พฤ' */
export function dowLabel(dow: unknown): string {
  const names = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']
  return typeof dow === 'number' ? (names[dow] ?? '') : String(dow ?? '')
}

/** ตัวเลือกเดือน — ใช้รายการที่ backend ส่งมา ไม่เดาเองว่ามีเดือนไหนบ้าง */
export function MonthPicker({
  value,
  months,
  onChange,
  disabled,
}: {
  value: string
  months: string[]
  onChange: (month: string) => void
  disabled?: boolean
}) {
  return (
    <label className="kv">
      <span className="kv-label">เดือน</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        {months.map((m) => (
          <option key={m} value={m}>
            {monthLabel(m)}
          </option>
        ))}
      </select>
    </label>
  )
}

/**
 * ตัวเลือก "รอบ" ตัดเงินเดือน (21 → 20) — หน้าเวลาทำงาน/OT ใช้ตัวนี้แทน MonthPicker
 *
 * รายการรอบมากับ response ของแต่ละหน้าอยู่แล้ว (field `rounds`) เลยไม่ต้องยิง
 * endpoint แยกมาโหลด — รอบที่กำลังแสดงจึงอยู่ในรายการเสมอ ไม่มีกรณี dropdown
 * ว่างหรือไม่มีค่าที่เลือกอยู่
 *
 * ⚠️ ป้ายรอบใช้ roundLabel จาก lib/approvals ตัวเดียวกับหน้า admin — ห้ามเขียน
 *    รูปแบบป้ายใหม่ที่นี่ ไม่งั้นรอบเดียวกันจะอ่านได้คนละแบบในสองหน้า
 */
export function RoundPicker({
  value,
  rounds,
  onChange,
  disabled,
}: {
  value: string
  rounds: Round[]
  onChange: (roundId: string) => void
  disabled?: boolean
}) {
  return (
    <label className="kv">
      <span className="kv-label">รอบ</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || rounds.length === 0}
      >
        {rounds.length === 0 && <option value="">— ไม่มีข้อมูลรอบ —</option>}
        {rounds.map((r) => (
          <option key={r.roundId} value={r.roundId}>
            {roundLabel(r)}
          </option>
        ))}
      </select>
    </label>
  )
}

/** หัวข้อหน้า + ปุ่มโหลดใหม่ + แถบ error — ท่าเดียวกันทั้งสามหน้า */
export function MeHead({
  title,
  meta,
  loading,
  error,
  onReload,
  children,
}: {
  title: string
  meta?: string
  loading: boolean
  error: string | null
  onReload: () => void
  children?: React.ReactNode
}) {
  return (
    <>
      <h1>{title}</h1>
      <section className="card">
        <div className="section-head">
          <div>
            {meta && <p className="meta">{meta}</p>}
            {children}
          </div>
          <button type="button" className="ghost" onClick={onReload} disabled={loading}>
            {loading ? 'กำลังโหลด…' : 'โหลดใหม่'}
          </button>
        </div>
        {error && <p className="error">{error}</p>}
      </section>
    </>
  )
}
